'use client';

import { useBunja } from 'bunja/react';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, type ReactNode } from 'react';

import { TradeMode } from '@flipster/apis/v2/trade/types';
import { useOverlay } from '@flipster/overlay';
import type { OpenPosition } from '@flipster/r230522/normal-sub/tables/private/positions';
import type { OpenPosition as LegacyOpenPosition } from '@flipster/r230522/original/position';
import { getFlipsterConfigsV1Key, getLocalStorage } from '@flipster/utils/localStorage';

import { authBunja } from '../shared/auth/state';
import { FullCloseModal } from './full-close';
import { PartialCloseModal } from './partial-close';
import { fireYourPositionsManageClickEvent } from './segment';

interface UseClosePositionModalOptions {
  onMarketCloseComplete?: () => void;
  onLimitCloseComplete?: () => void;
  /** Sub-account ID for copy trading. If omitted, treated as main account. */
  accountId?: number;
  /** Trade mode override for copy trading sub-accounts. If omitted, uses account websocket data. */
  tradeMode?: TradeMode;
  /** When true, only market close is allowed (hides limit close option). */
  marketOnly?: boolean;
}

export const useClosePositionModal = (options?: UseClosePositionModalOptions) => {
  const { onMarketCloseComplete, onLimitCloseComplete, accountId, tradeMode, marketOnly } = options ?? {};
  const overlay = useOverlay();

  // user는 segment 로깅 시점에만 필요 — useAuth() 구독(getMeAtom 등 8개 atom) 대신
  // 핸들러 자체를 useAtomCallback으로 만들어 호출 시점에 get으로 읽는다
  const { getMeAtom } = useBunja(authBunja);

  const onOpenFullCloseModal = useAtomCallback(
    useCallback(
      (get, _set, position: OpenPosition | LegacyOpenPosition, positionMode?: TradeMode) => {
        overlay.open(({ isOpen, close, unmount }) => (
          <FullCloseModal
            position={position}
            onMarketCloseComplete={onMarketCloseComplete}
            accountId={accountId}
            tradeMode={tradeMode}
            isOpen={isOpen}
            onClose={close}
            onExit={unmount}
          />
        ));

        const user = get(getMeAtom).data;
        if (!user || !positionMode) return;
        const key = getFlipsterConfigsV1Key(user.userId, 'showAllPerp');
        fireYourPositionsManageClickEvent('Full close', {
          symbol: position.symbol,
          side: position.side,
          initLeverage: position.initLeverage,
          unrealizedPnl: position.unrealizedPnl.toString(),
          calcUnrealizedPnlPercent: position.calcUnrealizedPnlPercent.toString(),
          fundsInvested: position.fundsInvested.toString(),
          airdropType: position.airdropType,
          mode: positionMode,
          isShowAll: getLocalStorage(key) ?? true,
          isProfitBoostMode: Boolean(position.boostExpiryTimestamp),
        });
      },
      [overlay, onMarketCloseComplete, accountId, tradeMode, getMeAtom],
    ),
  );

  const onOpenPartialCloseModal = useAtomCallback(
    useCallback(
      (
        get,
        _set,
        position: OpenPosition | LegacyOpenPosition,
        positionMode?: TradeMode,
        opts?: { title?: ReactNode },
      ) => {
        overlay.open(({ isOpen, close, unmount }) => (
          <PartialCloseModal
            position={position}
            isOpen={isOpen}
            onClose={close}
            onExit={unmount}
            onMarketCloseComplete={onMarketCloseComplete}
            onLimitCloseComplete={onLimitCloseComplete}
            accountId={accountId}
            tradeMode={tradeMode}
            marketOnly={marketOnly}
            title={opts?.title}
          />
        ));
        const user = get(getMeAtom).data;
        if (!user || !positionMode) return;

        const key = getFlipsterConfigsV1Key(user.userId, 'showAllPerp');
        fireYourPositionsManageClickEvent('Partial close', {
          symbol: position.symbol,
          side: position.side,
          initLeverage: position.initLeverage,
          unrealizedPnl: position.unrealizedPnl.toString(),
          calcUnrealizedPnlPercent: position.calcUnrealizedPnlPercent.toString(),
          fundsInvested: position.fundsInvested.toString(),
          airdropType: position.airdropType,
          mode: positionMode,
          isShowAll: getLocalStorage(key) ?? true,
          isProfitBoostMode: Boolean(position.boostExpiryTimestamp),
        });
      },
      [overlay, onMarketCloseComplete, onLimitCloseComplete, accountId, tradeMode, marketOnly, getMeAtom],
    ),
  );

  return {
    onOpenFullCloseModal,
    onOpenPartialCloseModal,
  };
};
